/* Date arithmetic.
   ================

   All of these build dates from PARTS rather than parsing an ISO string with
   new Date(iso). That is deliberate and it is the same trap every time: a bare
   "2026-08-17" is parsed as UTC midnight, which in California renders as the
   16th. Splitting on '-' and using the (year, month, day) constructor keeps
   everything in local time, which is what a CHW's calendar means.
*/

/* Whole days between two YYYY-MM-DD dates. Positive when a is later. */
export function daysBetween(a, b) {
  const [ya, ma, da] = a.split('-').map(Number);
  const [yb, mb, db] = b.split('-').map(Number);
  const A = new Date(ya, ma - 1, da).getTime();
  const B = new Date(yb, mb - 1, db).getTime();
  return Math.round((A - B) / 86400000);
}

/* Add months, CLAMPING to the end of the target month instead of rolling over.
   JavaScript turns 31 August plus six months into 3 March; a review due date
   six months after 31 August should be 28 February, not early March. The loop
   walks back until the month is the one that was asked for. */
export function addMonthsISO(iso, months) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setMonth(dt.getMonth() + months);
  while ((dt.getMonth() - (m - 1) - months + 12) % 12 !== 0) {
    dt.setDate(dt.getDate() - 1);
  }
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/* Month and day only — the year in a birthday is not the question being asked. */
export function isBirthdayToday(iso) {
  if (!iso) return false;
  const parts = iso.split('-');
  if (parts.length !== 3) return false;
  const m = Number(parts[1]), d = Number(parts[2]);
  const today = new Date();
  return m === (today.getMonth() + 1) && d === today.getDate();
}

/* "3 min ago", "2 days ago", then falls back to a plain date. Anything in the
   future reads as "just now" rather than a negative, because a clock a few
   seconds ahead is far more likely than a genuine future timestamp. */
export function relativeTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60000) return 'just now';
  if (ms < 3600000) return `${Math.floor(ms / 60000)} min ago`;
  if (ms < 86400000) return `${Math.floor(ms / 3600000)} hr ago`;
  if (ms < 7 * 86400000) {
    const d = Math.floor(ms / 86400000);
    return `${d} day${d === 1 ? '' : 's'} ago`;
  }
  return new Date(iso).toLocaleDateString();
}

/* Imported CSVs carry dates in whatever the spreadsheet felt like. Already-ISO
   passes through untouched; anything else gets one attempt at Date parsing and
   then gives up rather than guessing. An empty cell is not an error. */
export function parseCSVDate(s) {
  if (!s) return '';
  s = String(s).trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  return '';
}

/* Deleted mileage routes sit in a trash for a week before going for good. */
export const TRASH_RETENTION_DAYS = 7;

/* Days left before a trashed item is permanently removed. Rounded UP, so an
   item with six hours left still reads "1 day" rather than "0". */
export function expiresInDays(deletedAt) {
  const expiryMs = new Date(deletedAt).getTime() + TRASH_RETENTION_DAYS * 86400000;
  return Math.ceil((expiryMs - Date.now()) / 86400000);
}
